<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <i class="fas fa-credit-card"></i> Payment Setting
                        </h4>
                        <div class="action-btn">
                            <button @click="loadAll" class="btn btn-light">
                                <i class="fas fa-sync"></i> Reload
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Toggles -->
            <div class="card p-4 mb-3">
                <h5 class="mb-3"><i class="fas fa-toggle-on"></i> Metode Pembayaran Aktif</h5>
                <p class="text-muted small mb-3">
                    Jika Tripay sedang gangguan / ditutup, matikan Tripay dan aktifkan Transfer Manual.
                    User akan melihat opsi yang sesuai di halaman billing.
                </p>
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <div class="form-check form-switch p-3 border rounded">
                            <input v-model="tripayBool" type="checkbox" class="form-check-input" id="tripayChk">
                            <label class="form-check-label" for="tripayChk">
                                <strong>Tripay (Gateway Otomatis)</strong>
                                <div class="text-muted small">VA, e-wallet, QRIS via Tripay.</div>
                            </label>
                        </div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <div class="form-check form-switch p-3 border rounded">
                            <input v-model="manualBool" type="checkbox" class="form-check-input" id="manualChk">
                            <label class="form-check-label" for="manualChk">
                                <strong>Transfer Manual</strong>
                                <div class="text-muted small">User transfer ke rekening, upload bukti, admin verify.</div>
                            </label>
                        </div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <div class="form-check form-switch p-3 border rounded">
                            <input v-model="starqrisBool" type="checkbox" class="form-check-input" id="starqrisChk">
                            <label class="form-check-label" for="starqrisChk">
                                <strong>StarQRIS</strong>
                                <div class="text-muted small">QRIS dinamis via StarQRIS — scan & bayar otomatis.</div>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="mb-3">
                    <label>Instruksi untuk user <small class="text-muted">(tampil di halaman transfer manual)</small></label>
                    <textarea v-model="setting.instruction_text" class="form-control" rows="3"
                        placeholder="Setelah transfer, harap upload bukti dan tunggu verifikasi admin (max 1x24 jam)."></textarea>
                </div>
                <div class="mb-3">
                    <label>Kontak admin <small class="text-muted">(opsional — ditampilkan untuk bantuan)</small></label>
                    <input v-model="setting.contact_admin_for_help" class="form-control"
                        placeholder="Bantuan: chat WA 0812-xxxx-xxxx">
                </div>

                <div>
                    <button @click="saveSetting" :disabled="savingSetting" class="btn btn-primary">
                        <i class="fas fa-save"></i> {{ savingSetting ? 'Saving...' : 'Simpan Setting' }}
                    </button>
                </div>
            </div>

            <!-- StarQRIS credentials -->
            <div class="card p-4 mb-3">
                <h5 class="mb-3"><i class="fas fa-qrcode"></i> Konfigurasi StarQRIS</h5>
                <p class="text-muted small mb-3">
                    Credential disimpan di database. Kosongkan field secret jika tidak ingin mengubah nilai yang sudah tersimpan.
                    Backend tetap fallback ke env <code>STARQRIS_*</code> jika field DB kosong.
                </p>
                <div class="row">
                    <div class="col-md-12 mb-3">
                        <label>API URL</label>
                        <input v-model="starqrisForm.url" class="form-control"
                            placeholder="https://starqris.web.id/api/v1">
                    </div>
                    <div class="col-md-6 mb-3">
                        <label>API Key <small class="text-muted">(Merchant API Key, diawali <code>qris_</code>)</small></label>
                        <input v-model="starqrisForm.api_key" type="password" class="form-control"
                            :placeholder="starqrisMeta.has_api_key ? `(saved: ${starqrisMeta.api_key_masked} — kosongkan jika tidak ubah)` : 'qris_xxxxxxxx'">
                        <small v-if="starqrisMeta.has_api_key && !starqrisMeta.api_key_valid_format" class="text-danger">
                            API key tersimpan tidak valid (harus diawali qris_). Isi ulang dari dashboard StarQRIS → API Keys.
                        </small>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label>Webhook Secret</label>
                        <input v-model="starqrisForm.webhook_secret" type="password" class="form-control"
                            :placeholder="starqrisMeta.has_webhook_secret ? `(saved: ${starqrisMeta.webhook_secret_masked} — kosongkan jika tidak ubah)` : 'paste webhook secret'">
                    </div>
                    <div class="col-md-6 mb-3">
                        <label>QRIS Account ID <small class="text-muted">(opsional)</small></label>
                        <input v-model="starqrisForm.qris_account_id" class="form-control" placeholder="123">
                    </div>
                    <div class="col-md-6 mb-3">
                        <label>Status credential</label>
                        <div class="form-control-plaintext">
                            <span :class="starqrisMeta.configured ? 'badge bg-success' : (starqrisMeta.has_api_key ? 'badge bg-danger' : 'badge bg-warning text-dark')">
                                {{ starqrisMeta.configured ? 'API key tersimpan & valid' : (starqrisMeta.has_api_key ? 'API key invalid' : 'API key belum diisi') }}
                            </span>
                        </div>
                    </div>
                    <div class="col-md-12 mb-3">
                        <label>Webhook URL <small class="text-muted">(daftarkan di dashboard StarQRIS)</small></label>
                        <div class="input-group">
                            <input :value="starqrisMeta.webhook_url" class="form-control" readonly>
                            <button type="button" class="btn btn-outline-secondary" @click="copyWebhookUrl">
                                <i class="fas fa-copy"></i> Copy
                            </button>
                        </div>
                    </div>
                </div>
                <div>
                    <button @click="saveSetting" :disabled="savingSetting" class="btn btn-primary">
                        <i class="fas fa-save"></i> {{ savingSetting ? 'Saving...' : 'Simpan StarQRIS' }}
                    </button>
                </div>
            </div>

            <!-- Bank accounts -->
            <div class="card p-4 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <h5 class="mb-0"><i class="fas fa-university"></i> Rekening Bank</h5>
                    <button @click="openBankModal()" class="btn btn-success btn-sm">
                        <i class="fas fa-plus"></i> Tambah Rekening
                    </button>
                </div>
                <p class="text-muted small mb-3">
                    Rekening yang aktif akan ditampilkan ke user saat memilih transfer manual.
                </p>

                <div class="table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Bank</th>
                                <th>No. Rekening</th>
                                <th>Atas Nama</th>
                                <th>Aktif</th>
                                <th>Urutan</th>
                                <th>Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="banks.length === 0">
                                <td colspan="7" class="text-center text-muted">Belum ada rekening</td>
                            </tr>
                            <tr v-for="(b, i) in banks" :key="b.id">
                                <td>{{ i + 1 }}</td>
                                <td>
                                    <strong>{{ b.bank_name }}</strong>
                                    <div v-if="b.bank_code" class="text-muted small">{{ b.bank_code }}</div>
                                </td>
                                <td><code>{{ b.account_number }}</code></td>
                                <td>{{ b.account_holder }}</td>
                                <td>
                                    <span :class="b.enabled === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                                        {{ b.enabled === 1 ? 'Aktif' : 'Nonaktif' }}
                                    </span>
                                </td>
                                <td>{{ b.sort_order }}</td>
                                <td>
                                    <button @click="openBankModal(b)" class="btn btn-sm btn-info mr-1">
                                        <i class="fas fa-edit"></i>
                                    </button>
                                    <button @click="deleteBank(b)" class="btn btn-sm btn-danger">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Bank Modal -->
            <div v-if="bankModalOpen" class="custom-modal-overlay" @click.self="bankModalOpen = false">
                <div class="custom-modal-content">
                    <div class="card p-4">
                        <h5 class="mb-3">
                            <i class="fas fa-university"></i>
                            {{ bankForm.id ? 'Edit Rekening' : 'Tambah Rekening' }}
                        </h5>
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label>Bank Name *</label>
                                <input v-model="bankForm.bank_name" class="form-control" placeholder="BCA / Mandiri / BNI">
                            </div>
                            <div class="col-md-6 mb-3">
                                <label>Bank Code <small class="text-muted">(opsional)</small></label>
                                <input v-model="bankForm.bank_code" class="form-control" placeholder="BCA / MANDIRI / BNI">
                            </div>
                            <div class="col-md-6 mb-3">
                                <label>No. Rekening *</label>
                                <input v-model="bankForm.account_number" class="form-control" placeholder="1234567890">
                            </div>
                            <div class="col-md-6 mb-3">
                                <label>Atas Nama *</label>
                                <input v-model="bankForm.account_holder" class="form-control" placeholder="PT StarSender Indonesia">
                            </div>
                            <div class="col-md-8 mb-3">
                                <label>Logo URL <small class="text-muted">(opsional)</small></label>
                                <input v-model="bankForm.logo_url" class="form-control" placeholder="https://...logo.png">
                            </div>
                            <div class="col-md-2 mb-3">
                                <label>Urutan</label>
                                <input v-model.number="bankForm.sort_order" type="number" class="form-control">
                            </div>
                            <div class="col-md-2 mb-3">
                                <label>Aktif</label>
                                <select v-model.number="bankForm.enabled" class="form-control">
                                    <option :value="1">Aktif</option>
                                    <option :value="0">Nonaktif</option>
                                </select>
                            </div>
                            <div class="col-md-12 mb-3">
                                <label>Catatan <small class="text-muted">(opsional, ditampilkan ke user)</small></label>
                                <textarea v-model="bankForm.note" class="form-control" rows="2"
                                    placeholder="BCA Pusat — atas nama starsender"></textarea>
                            </div>
                        </div>
                        <div class="d-flex gap-2">
                            <button @click="saveBank" :disabled="savingBank" class="btn btn-primary mr-2">
                                <i class="fas fa-save"></i> {{ savingBank ? 'Saving...' : 'Simpan' }}
                            </button>
                            <button @click="bankModalOpen = false" class="btn btn-secondary">
                                Batal
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    data() {
        return {
            savingSetting: false,
            savingBank: false,
            bankModalOpen: false,
            setting: {
                tripay_enabled: 1,
                manual_transfer_enabled: 0,
                starqris_enabled: 0,
                instruction_text: '',
                contact_admin_for_help: '',
            },
            starqrisForm: {
                url: 'https://starqris.web.id/api/v1',
                api_key: '',
                webhook_secret: '',
                qris_account_id: '',
            },
            starqrisMeta: {
                api_key_masked: '',
                has_api_key: false,
                api_key_valid_format: true,
                webhook_secret_masked: '',
                has_webhook_secret: false,
                webhook_url: 'https://api.starsender.online/api/v1/payment/callback/starqris',
                configured: false,
            },
            banks: [],
            bankForm: this.emptyBankForm(),
        };
    },
    computed: {
        tripayBool: {
            get() { return this.setting.tripay_enabled === 1; },
            set(v) { this.setting.tripay_enabled = v ? 1 : 0; },
        },
        manualBool: {
            get() { return this.setting.manual_transfer_enabled === 1; },
            set(v) { this.setting.manual_transfer_enabled = v ? 1 : 0; },
        },
        starqrisBool: {
            get() { return this.setting.starqris_enabled === 1; },
            set(v) { this.setting.starqris_enabled = v ? 1 : 0; },
        },
    },
    mounted() {
        this.loadAll();
    },
    methods: {
        emptyBankForm() {
            return {
                id: 0,
                bank_name: '',
                bank_code: '',
                account_number: '',
                account_holder: '',
                logo_url: '',
                sort_order: 0,
                enabled: 1,
                note: '',
            };
        },
        notify(type, msg) {
            if (this.$toast) {
                this.$toast.open({ message: msg, type, duration: type === 'error' ? 6000 : 3000, position: 'top-right' });
            } else {
                window.alert(msg);
            }
        },
        errMsg(e, fb) {
            return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || fb;
        },
        async loadAll() {
            await Promise.all([this.loadSetting(), this.loadBanks()]);
        },
        async loadSetting() {
            try {
                const r = await axios.get('admin/payment/setting');
                if (r.data && r.data.success) {
                    this.setting = r.data.data.setting || this.setting;
                    const sq = r.data.data.starqris || {};
                    this.starqrisMeta = {
                        api_key_masked: sq.api_key_masked || '',
                        has_api_key: !!sq.has_api_key,
                        api_key_valid_format: sq.api_key_valid_format !== false,
                        webhook_secret_masked: sq.webhook_secret_masked || '',
                        has_webhook_secret: !!sq.has_webhook_secret,
                        webhook_url: sq.webhook_url || this.starqrisMeta.webhook_url,
                        configured: !!sq.configured,
                    };
                    this.starqrisForm.url = sq.url || 'https://starqris.web.id/api/v1';
                    this.starqrisForm.api_key = '';
                    this.starqrisForm.webhook_secret = '';
                    this.starqrisForm.qris_account_id = sq.qris_account_id || '';
                }
            } catch (e) {
                this.notify('error', 'Gagal load setting: ' + this.errMsg(e, ''));
            }
        },
        async copyWebhookUrl() {
            const url = this.starqrisMeta.webhook_url;
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(url);
                } else {
                    const el = document.createElement('textarea');
                    el.value = url;
                    document.body.appendChild(el);
                    el.select();
                    document.execCommand('copy');
                    document.body.removeChild(el);
                }
                this.notify('success', 'Webhook URL disalin');
            } catch (e) {
                this.notify('error', 'Gagal copy URL');
            }
        },
        async loadBanks() {
            try {
                const r = await axios.get('admin/payment/banks');
                if (r.data && r.data.success) {
                    this.banks = r.data.data.banks || [];
                }
            } catch (e) {
                this.notify('error', 'Gagal load banks: ' + this.errMsg(e, ''));
            }
        },
        async saveSetting() {
            this.savingSetting = true;
            try {
                const r = await axios.post('admin/payment/setting', {
                    tripay_enabled: this.setting.tripay_enabled,
                    manual_transfer_enabled: this.setting.manual_transfer_enabled,
                    starqris_enabled: this.setting.starqris_enabled,
                    instruction_text: this.setting.instruction_text || '',
                    contact_admin_for_help: this.setting.contact_admin_for_help || '',
                    starqris_url: this.starqrisForm.url || 'https://starqris.web.id/api/v1',
                    starqris_api_key: this.starqrisForm.api_key || '',
                    starqris_webhook_secret: this.starqrisForm.webhook_secret || '',
                    starqris_qris_account_id: this.starqrisForm.qris_account_id || '',
                });
                if (r.data && r.data.success) {
                    this.notify('success', 'Setting tersimpan');
                    await this.loadSetting();
                } else {
                    this.notify('error', 'Gagal: ' + (r.data && r.data.message));
                }
            } catch (e) {
                this.notify('error', 'Gagal: ' + this.errMsg(e, ''));
            } finally {
                this.savingSetting = false;
            }
        },
        openBankModal(b = null) {
            if (b) {
                this.bankForm = JSON.parse(JSON.stringify(b));
                this.bankForm.bank_code = this.bankForm.bank_code || '';
                this.bankForm.logo_url = this.bankForm.logo_url || '';
                this.bankForm.note = this.bankForm.note || '';
            } else {
                this.bankForm = this.emptyBankForm();
            }
            this.bankModalOpen = true;
        },
        async saveBank() {
            if (!this.bankForm.bank_name || !this.bankForm.account_number || !this.bankForm.account_holder) {
                this.notify('warning', 'bank_name, account_number, account_holder wajib diisi');
                return;
            }
            this.savingBank = true;
            try {
                const payload = { ...this.bankForm };
                if (!payload.id) delete payload.id;
                const r = await axios.post('admin/payment/banks', payload);
                if (r.data && r.data.success) {
                    this.notify('success', 'Bank tersimpan');
                    this.bankModalOpen = false;
                    await this.loadBanks();
                } else {
                    this.notify('error', 'Gagal: ' + (r.data && r.data.message));
                }
            } catch (e) {
                this.notify('error', 'Gagal: ' + this.errMsg(e, ''));
            } finally {
                this.savingBank = false;
            }
        },
        async deleteBank(b) {
            if (!window.confirm(`Hapus rekening "${b.bank_name} - ${b.account_number}" ?`)) return;
            try {
                const r = await axios.delete('admin/payment/banks/' + b.id);
                if (r.data && r.data.success) {
                    this.notify('success', 'Bank dihapus');
                    await this.loadBanks();
                } else {
                    this.notify('error', 'Gagal: ' + (r.data && r.data.message));
                }
            } catch (e) {
                this.notify('error', 'Gagal: ' + this.errMsg(e, ''));
            }
        },
    },
};
</script>

<style scoped>
.gap-2 > * + * { margin-left: 8px; }
.custom-modal-overlay {
    position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5);
    z-index: 1050; display: flex; align-items: center; justify-content: center;
}
.custom-modal-content {
    width: 100%; max-width: 720px; margin: 16px;
}
</style>
